import { CircleOff } from "lucide-react";

import type { LookupResponse, ResolutionType } from "@sirdab/returns-shared";
import { Alert, EmptyState } from "@sirdab/ui";

import {
  ChoiceGroup,
  ChoiceOption,
  FeeDisclosure,
  StepActions,
} from "../components";
import { useFlow } from "../flow";
import { useMessages } from "../i18n";
import { exchangeAvailable, feeTotalHalalas, RequireLookup } from "./shared";
import { StepScaffold } from "./StepScaffold";

const ORDER: readonly ResolutionType[] = ["refund", "exchange", "credit"];

const LABEL_KEY = {
  refund: "resolution.refund",
  exchange: "resolution.exchange",
  credit: "resolution.credit",
} as const;

/**
 * Step 5 of docs/08 — what the shopper is asking for.
 *
 * Three rules meet here:
 *   - only the merchant's enabled resolutions appear (policy check 8);
 *   - exchange is withheld for a line whose replacement is out of stock — shown disabled
 *     with the reason rather than silently vanishing;
 *   - the fee is disclosed HERE, before submit, and again at review (policy check 9).
 *
 * All three disabled is an honest empty state, not a fake choice. And nothing on this
 * screen promises money moving or a date for it: the choice is a request (ADR-003).
 */
function Body({ lookup }: { lookup: LookupResponse }): React.JSX.Element {
  const { t } = useMessages();
  const { lines: drafts, resolution, update, next, back } = useFlow();

  const enabled = ORDER.filter((option) => lookup.policy.resolutions[option]);
  const inStock = exchangeAvailable(drafts, lookup.order.lines);
  const fee = feeTotalHalalas(lookup.policy, drafts, lookup.order.lines);

  if (enabled.length === 0) {
    return (
      <EmptyState
        icon={CircleOff}
        title={t("portal.no_resolutions_enabled")}
        className="py-8"
      />
    );
  }

  // A choice carried over from an earlier lookup may no longer be on offer.
  const selectable = (option: ResolutionType): boolean =>
    enabled.includes(option) && (option !== "exchange" || inStock);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-body text-muted-foreground">
        {t("resolution.select_hint")}
      </p>

      <ChoiceGroup
        value={resolution}
        onChange={(option) => update({ resolution: option as ResolutionType })}
      >
        {enabled.map((option) => (
          <ChoiceOption
            key={option}
            value={option}
            checked={resolution === option}
            disabled={!selectable(option)}
            label={t(LABEL_KEY[option])}
            {...(selectable(option)
              ? {}
              : { unavailableReason: t("resolution.exchange_unavailable") })}
          />
        ))}
      </ChoiceGroup>

      {/* Disclosure point one of two. */}
      <FeeDisclosure totalHalalas={fee} point="resolution" />

      <Alert>{t("portal.resolution_is_a_request")}</Alert>

      <StepActions
        onNext={next}
        onBack={back}
        nextDisabled={resolution === null || !selectable(resolution)}
      />
    </div>
  );
}

export function ResolutionScreen(): React.JSX.Element {
  return (
    <StepScaffold
      step="resolution"
      titleKey="screen.resolution.title"
      descriptionKey="screen.resolution.description"
    >
      <RequireLookup>{(lookup) => <Body lookup={lookup} />}</RequireLookup>
    </StepScaffold>
  );
}
